import * as React from "react";
import {useEffect, useRef} from "react";
import {translate} from "../../../translate/translate";
import {
    Box,
    Button, CircularProgress,
    Fab,
    FormControl,
    Grid,
    InputAdornment,
    Paper,
    TextField,
    Typography
} from "@mui/material";
import TablePaginationComponent from "../../../components/table/table-pagination.component";
import {Add, Download, Search} from "@mui/icons-material";
import {toast} from "react-toastify";
import {PaginatedResult} from "../../../models/default/paginated-result";
import TableActionComponent from "../../../components/table/table-action.component";
import {Field, Form, Formik, FormikHelpers} from "formik";
import {ErrorComponent} from "../../../components/error/error.component";
import GenericDialog from "../../../components/dialog/generic-dialog.component";
import * as Yup from "yup";
import {useFilters} from "../../../filters/base-filter";
import {usePagination} from "../../../utils/pagination";
import {FtBomGroupFilter, FtBomGroupService} from "../../../services/ft-bom-group-service";
import {FtBomGroup} from "../../../models/basic/ft-bom-group";
import CollapsibleTableComponent from "../../../components/table/collapsible-table.component";
import AssociateMaterialsComponent from "./associate-materials.component";
import {FtBom} from "../../../models/basic/ft-bom";
import {orderByFn} from "../../../utils/order-by";
import {trackPromise} from "react-promise-tracker";


const FtBomGroupComponent = () => {
    const service = new FtBomGroupService();
    const {filters, updateFilter, updateFilterKeepingParam, resetFilters } = useFilters<FtBomGroupFilter>(
        { search: "", ordering: "code"}, ["search", "ordering"]
    );

    const [groupList, setGroupList] = React.useState<PaginatedResult<FtBomGroup>>();
    const [loadingDownload, setLoadingDownload] = React.useState(false);
    const pollingRef = useRef<NodeJS.Timeout | null>(null);
    const {
        dataSource,
        pagination,
        resetPage,
        handleChangePage,
        handleChangeRowsPerPage
    } = usePagination<FtBomGroup>(groupList);

    const [object, setObject] = React.useState(new FtBomGroup());
    const [openDialog, setOpenDialog] = React.useState(false);
    const [openDialogMaterials, setOpenDialogMaterials] = React.useState(false);
    const [invalidForm, setInvalidForm] = React.useState(true);
    const [isEdit, setIsEdit] = React.useState(false);

    const [orderDirection, setOrderDirection] = React.useState<'asc' | 'desc' | ''>('asc');

    const schema = Yup.object().shape({
        code: Yup.string().required(),
        description: Yup.string().required(),
    });

    const handleOpenNew = () => {
        setIsEdit(false);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setInvalidForm(true);
        setOpenDialog(false);
        setObject(new FtBomGroup());
    };

    const search = (restartIndex = false) => {
        service.readPaginated(filters, [], pagination).then(response => {
            setGroupList(response.data);
        }).catch((error: any) => {
            ErrorComponent(error);
        });

        restartIndex && resetPage();
    };

    useEffect(() => {
        search();
    }, [pagination, filters.ordering]);
    
    const handleSubmit = (values: FtBomGroup) => {
        setTimeout(() => {
            const group = new FtBomGroup();
            const formattedValues = {
                ...values,
                code: values.code.trim(),
                description: values.description?.trim(),
            };
            Object.assign(group, formattedValues);
            if (group.id !== undefined) {
                service.patch(group.id, group).then(() => {
                    toast.success(translate.t("successfully_updated"));
                    handleCloseDialog();
                    search(true);
                }).catch((error: any) => {
                    ErrorComponent(error);
                });
            } else {
                const { id: _, ...newObj } = group;
                service.create(newObj as any)
                    .then(() => {
                        toast.success(translate.t("successfully_registered"));
                        handleCloseDialog();
                        search(true);
                    })
                    .catch((error: any) => {
                        ErrorComponent(error);
                    });
            }
        }, 500);
    }

    const stopPollingFn = () => {
        if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
        }
    };

    const export_xlsx = (hash?: string) => {
        search();
        setLoadingDownload(true);

        service.export_xlsx(filters).then(response => {
            if (response.data.hash) {
                get_export_status(response.data.hash);
            }
        }).catch((error: any) => {
            ErrorComponent(error);
            setLoadingDownload(false);
        })
    }

    const get_export_status = (hash: string) => {
        stopPollingFn();
        pollingRef.current = setInterval(() => {
            trackPromise(
                service.get_export_status(hash).then(response => {
                    if (response.data.status === 2 || response.data.status === 3) {
                        stopPollingFn();
                        response.data.status === 2 && download(hash);
                    }
                }).catch((error: any) => {
                    ErrorComponent(error);
                    setLoadingDownload(false);
                })
            )
        }, 5000)
    }

    const download = (hash: string) => {
        service.download({hash}, '/download').then(response => {
            service.downloadFile(response);
        }).finally(() => {
            setLoadingDownload(false);
        });
    }

    const handleObjChange = (group: FtBomGroup) => {
        setObject(group);
        setInvalidForm(!group.code.trim() || !group.description?.trim());
    }
  
    const handleDelete = (row: FtBomGroup) => {
        service.delete(Number(row.id)).then(() => {
            toast.success(translate.t("successfully_deleted"));
            resetFilters();
            resetPage();
            search();
        }).catch((error: any) => {
            ErrorComponent(error);
        });
    };
    
    const handleDeleteMaterial = (row: FtBom) => {
        service.disassociateGroup(row.foxconn_part_number!).then(()=> {
            toast.success(translate.t("successfully_deleted"));
            resetFilters();
            resetPage();
            search();
        }).catch((error: any) => {
            ErrorComponent(error);
        });
    }
  
    const layoutDetail = (row: any) => {
        return (
            <TablePaginationComponent
                key={row.foxconn_part_number}
                state={row.materials}
                displayedColumnsShow={["FOXCONN PN", "Descrição EN", "Descrição PT", "action"]}
                displayedColumns={["foxconn_part_number", "description_english", "description_portuguese", "action"]}
                columnStyles={{1:"30%", 2:"30%", 3:"30%"}}
                showActions={["delete"]}
                handleDelete={handleDeleteMaterial}
            />
        )
    }
  
    const handleOpenEdit = (row: FtBomGroup) => {
        setIsEdit(true);
        setObject(row);
        setInvalidForm(false);
        setOpenDialog(true);
    }
  
    const handleOpenMaterialsDialog = (row: FtBomGroup) => {
        setObject(row);
        setOpenDialogMaterials(true);
    }
  
    const handleCloseMaterialsDialog = () => {
        setObject(new FtBomGroup());
        setOpenDialogMaterials(false);
        search();
    }

    return (
        <section>
            <div className="floating-title" style={{display: "flex", flexDirection: "row", alignItems: "center"}}>
                <Typography variant={"h5"}>
                    {translate.t("ft_bom_groups")}
                </Typography>
            </div>
            <div className="floating-button">
                <Button
                    color="secondary"
                    variant="contained"
                    startIcon={<Add/>}
                    onClick={() => {
                        handleOpenNew();
                    }}>{translate.t("new_ft_bom_group").toUpperCase()}</Button>
            </div>

            <Grid size={12}>
                <Paper variant="outlined">
                    <Box p={2}>
                        <Formik
                            enableReinitialize={true}
                            validateOnMount={true}
                            initialValues={{name: ""}}
                            onSubmit={(values, {setSubmitting}) => {
                                setTimeout(() => {
                                    search(true);
                                    setSubmitting(false);
                                }, 500);
                            }}
                        >
                            {
                                () => (
                                    <Form autoComplete="off">
                                        <Box alignItems="center" display="flex" justifyContent="space-between">
                                            <Grid alignItems="center" container spacing={2} direction={"row"} width="50%">
                                                <Grid size={7}>
                                                    <Field
                                                        as={TextField}
                                                        name="search"
                                                        label={""}
                                                        fullWidth
                                                        variant={"outlined"}
                                                        autoFocus={true}
                                                        placeholder={translate.t("search_ft_bom_group")}
                                                        value={filters.search}
                                                        onChange={(event: React.ChangeEvent<{
                                                            value: unknown
                                                        }>) => updateFilterKeepingParam("search", event.target.value)}
                                                    />
                                                </Grid>
                                                <Grid >
                                                    <Fab
                                                        size="small"
                                                        type="submit"
                                                        color="secondary">
                                                        <Search/>
                                                    </Fab>
                                                </Grid>
                                            </Grid>
                                            <Grid alignItems="center" container justifyContent="flex-end" spacing={3}
                                                  width="20%">
                                                <Grid >
                                                    <Button
                                                        disabled={loadingDownload}
                                                        color="secondary"
                                                        variant="contained"
                                                        startIcon={<Download/>}
                                                        onClick={() => {
                                                            export_xlsx();
                                                        }}>{translate.t("export").toUpperCase()}
                                                        {loadingDownload &&
                                                            <CircularProgress size="20px" sx={{marginLeft: '10px'}}/>}
                                                    </Button>
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    </Form>
                                )}
                        </Formik>
                    </Box>
                </Paper>
            </Grid>
          
            <Paper variant="outlined" sx={{marginTop: "16px"}}>
                <CollapsibleTableComponent
                    state={dataSource}
                    displayedColumnsShow={["Código do grupo", "description", "action"]}
                    displayedColumns={["code", "description", "action"]}
                    handleDelete={handleDelete}
                    handleAddChild={handleOpenMaterialsDialog}
                    editWithDialog={handleOpenEdit}
                    columnStyles={{1:"20%", 2:"35%", 3:"35%", 4:"10%"}}
                    actionsComponent={TableActionComponent}
                    childrenComponent={layoutDetail}
                    count={groupList?.count}
                    next={groupList?.next}
                    previous={groupList?.previous}
                    page={pagination.page}
                    onPageChange={handleChangePage}
                    rowsPerPage={pagination.limit}
                    showActions={["add_child", "edit", "delete"]}
                    actionsHints={["Adicionar material", "edit", "delete"]}
                    handleChangeRowsPerPage={handleChangeRowsPerPage}
                    rowsPerPageOptions={[10, 50, 100]}
                    orderByColumns={["code"]}
                    orderBy={filters.ordering}
                    orderByFn={(column: string, orderDirection: any) => {
                        orderByFn(column, orderDirection, filters.ordering || '', setOrderDirection, updateFilterKeepingParam);
                    }}
                    orderDirection={orderDirection}
                />
            </Paper>
            <GenericDialog
                open={openDialog}
                onClose={handleCloseDialog}
                title={isEdit ? translate.t("edit_ft_bom_group") : translate.t("new_ft_bom_group")}
                colorButton={"primary"}
                labelButton={"save"}
                disableConfirm={invalidForm}
                handleConfirm={() => handleSubmit(object)}>
                <Box>
                    <FormControl fullWidth>
                        <Formik
                            id="dialog-form"
                            enableReinitialize
                            initialValues={object}
                            validationSchema={schema}
                            onSubmit={(values: FtBomGroup, {resetForm}: FormikHelpers<FtBomGroup>) => {
                                handleSubmit(values);
                                resetForm();
                                handleCloseDialog();
                            }}
                            validateOnChange={true}>
                            {({isValid, values, isValidating, errors, touched}) => (
                                <Form autoComplete="off">
                                    <Grid container spacing={2} direction={"column"}>
                                        <Grid >
                                            <Field
                                                as={TextField}
                                                name="code"
                                                label={translate.t("code")}
                                                variant={"outlined"}
                                                fullWidth
                                                autoFocus={true}
                                                required={true}
                                                values={values}
                                                error={touched.code && Boolean(errors.code) && invalidForm}
                                                onChange={(event: React.ChangeEvent<{
                                                    value: unknown
                                                }>) => {
                                                    const obj = object;
                                                    const value = event.target.value as string;
                                                    if (value.length > 256) return;
                                                    handleObjChange({
                                                        ...obj,
                                                        code: value as string,
                                                    });
                                                }}
                                                InputProps={{
                                                    maxLength: 54,
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            {touched.code && Boolean(errors.code) && invalidForm && (
                                                                <div className={"required-field"}>
                                                                    {translate.t("required_field")}
                                                                </div>
                                                            )}
                                                        </InputAdornment>
                                                    ),
                                                }}/>
                                        </Grid>
                                        <Grid >
                                            <Field
                                                as={TextField}
                                                name="description"
                                                label={translate.t("description")}
                                                variant={"outlined"}
                                                fullWidth
                                                autoFocus={false}
                                                required={true}
                                                values={values}
                                                error={touched.description && Boolean(errors.description) && invalidForm}
                                                minRows={4}
                                                multiline
                                                onChange={(event: React.ChangeEvent<{
                                                    value: unknown
                                                }>) => {
                                                    const obj = object;
                                                    const value = event.target.value as string;
                                                    if (value.length > 256) return;
                                                    handleObjChange({
                                                        ...obj,
                                                        description: value as string,
                                                    })
                                                }}
                                                InputProps={{
                                                    maxLength: 54,
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            {touched.description && Boolean(errors.description) && invalidForm && (
                                                                <div className={"required-field"}>
                                                                    {translate.t("required_field")}
                                                                </div>
                                                            )}
                                                        </InputAdornment>
                                                    ),
                                                }}/>
                                        </Grid>
                                    </Grid>
                                </Form>
                            )}
                        </Formik>
                    </FormControl>
                </Box>
            </GenericDialog>
            <AssociateMaterialsComponent
                openDialog={openDialogMaterials}
                group={object}
                handleCloseDialog={handleCloseMaterialsDialog}
            />
        </section>
    );
};

export default FtBomGroupComponent;
